/** Persist minimal cart lines before Paymob redirect; read back after return (Redux cart may not be loaded yet). */
export const POST_CHECKOUT_CART_SNAPSHOT_KEY = 'flea_post_checkout_cart_snapshot';

/** Handoff to Orders page for “more from this seller” UI. */
export const POST_CHECKOUT_SELLER_CATALOG_KEY = 'flea_post_checkout_seller_catalog';

export const POST_CHECKOUT_DELIVERY_NOTE =
  'Delivery is usually still about the same when more items from this seller ship together.';

export function snapshotCartLinesForPostCheckoutReco(lines) {
  return (lines || []).map((l) => ({
    listing_id: l.listing_id ?? null,
    listing_type: l.listing_type ?? null,
    customization_request_id: l.customization_request_id ?? null,
    seller_id: l.seller_id,
    price_at_time: l.price_at_time,
    line_kind: l.line_kind,
    item_details: l.item_details
      ? {
          is_anonymous: l.item_details.is_anonymous,
          seller_display_username: l.item_details.seller_display_username,
          seller_display_name: l.item_details.seller_display_name,
        }
      : null,
  }));
}

function sellerLabelFromDetails(details) {
  const uname = details?.seller_display_username;
  const name = details?.seller_display_name;
  if (uname) {
    const s = String(uname);
    return s.startsWith('@') ? s : `@${s}`;
  }
  if (name) return String(name);
  return null;
}

/**
 * After checkout: for each marketplace seller with a non-anonymous listing under threshold EGP/unit,
 * fetch their public catalogue and drop lines purchased in this order.
 * @param {Array} lines — cart lines (full or snapshot shape)
 * @param {(ownerId: number, page?: number, pageSize?: number) => Promise<Array>} getPublicUserItems
 */
export async function buildPostCheckoutSellerCatalogRecommendations(lines, getPublicUserItems, options = {}) {
  const priceThresholdEgp = options.priceThresholdEgp ?? 50;
  const maxItemsPerSeller = options.maxItemsPerSeller ?? 12;
  const cataloguePageSize = options.cataloguePageSize ?? 48;

  const qualifyingLabels = new Map();
  for (const line of lines || []) {
    if (line.line_kind === 'designer') continue;
    const unit = parseFloat(line.price_at_time ?? line.price ?? NaN);
    if (!(unit < priceThresholdEgp)) continue;
    if (line.item_details?.is_anonymous !== false) continue;
    const sid = line.seller_id;
    if (sid == null || sid === '') continue;
    if (!qualifyingLabels.has(sid)) {
      const label = sellerLabelFromDetails(line.item_details) || `Seller #${sid}`;
      qualifyingLabels.set(sid, label);
    }
  }

  if (!qualifyingLabels.size) return null;

  const purchasedBySeller = new Map();
  for (const line of lines || []) {
    if (!line?.listing_id || line.line_kind === 'designer') continue;
    const sid = line.seller_id;
    if (sid == null || sid === '') continue;
    if (!purchasedBySeller.has(sid)) purchasedBySeller.set(sid, new Set());
    purchasedBySeller.get(sid).add(Number(line.listing_id));
  }

  const chunks = await Promise.all(
    [...qualifyingLabels.entries()].map(async ([sellerId, sellerLabel]) => {
      try {
        const catalogue = await getPublicUserItems(sellerId, 1, cataloguePageSize);
        const bought = purchasedBySeller.get(sellerId) || new Set();
        const filtered = (catalogue || [])
          .filter((it) => it?.id != null && !bought.has(Number(it.id)))
          .slice(0, maxItemsPerSeller)
          .map((it) => ({
            id: it.id,
            title: it.title,
            price: it.price,
            primary_image: it.primary_image,
            category: it.category,
            in_stock: it.in_stock,
          }));
        if (!filtered.length) return null;
        return { sellerId, sellerLabel, items: filtered };
      } catch {
        return null;
      }
    }),
  );

  const sellers = chunks.filter(Boolean).sort((a, b) => a.sellerId - b.sellerId);
  if (!sellers.length) return null;

  return {
    deliveryNote: POST_CHECKOUT_DELIVERY_NOTE,
    sellers,
  };
}
